import { useEffect, useState } from 'react';
import { Notice, PageHead, formatDateTime } from '../components/ui.js';
import { describeChange, rejectionMessage } from '../offline/describe.js';
import { useOffline } from '../offline/context.js';
import type { OutboxItem, PhotoItem } from '../offline/store.js';

/**
 * Changes not sent: what waits on this device, and what the server refused.
 * Nothing here is ever deleted by the portal (brief section 9).
 */
export function Changes() {
  const { store, unavailable, counts, sending, outcome, sendNow } = useOffline();
  const [items, setItems] = useState<OutboxItem[] | null>(null);
  const [photos, setPhotos] = useState<PhotoItem[]>([]);

  useEffect(() => {
    if (!store) return;
    let current = true;
    void store.outbox().then((list) => current && setItems(list));
    void store.photos().then((list) => current && setPhotos(list));
    return () => {
      current = false;
    };
  }, [store, counts]);

  // Scanned cards: their photo waits with the other changes (brief section 9).
  const lines = [
    ...(items ?? []).map((i) => ({
      key: i.opId,
      status: i.status,
      title: describeChange(i.op),
      createdAt: i.createdAt,
      error: i.error,
    })),
    ...photos.map((p) => ({
      key: p.contactId,
      status: p.status,
      title: 'Card photo, to be read',
      createdAt: p.createdAt,
      error: p.error,
    })),
  ].sort((a, b) => a.createdAt - b.createdAt);
  const waiting = lines.filter((i) => i.status === 'pending');
  const refused = lines.filter((i) => i.status === 'rejected');

  return (
    <>
      <PageHead
        title="Changes not sent"
        lede="Everything you save is kept on this device first, encrypted, then sent to the portal by itself when there is network. Nothing here is ever deleted."
      />
      {unavailable && (
        <Notice kind="info">
          This browser keeps nothing on the device: changes go straight to the portal.
        </Notice>
      )}
      {outcome === 'offline' && waiting.length > 0 && (
        <Notice kind="info">No network: the changes wait on this device.</Notice>
      )}
      {outcome === 'server_error' && waiting.length > 0 && (
        <Notice kind="info">The portal does not answer: the changes wait on this device.</Notice>
      )}
      {outcome === 'needs_sign_in' && (
        <Notice kind="info">Your session has ended: sign in again to send the changes.</Notice>
      )}
      {items && lines.length === 0 && <Notice kind="ok">Everything is sent.</Notice>}

      {waiting.length > 0 && (
        <section className="section">
          <div className="section-head">
            <h2 className="sec">Waiting ({waiting.length})</h2>
            <button
              type="button"
              className="btn primary sm"
              disabled={sending}
              onClick={() => void sendNow()}
            >
              {sending ? 'Sending' : 'Send now'}
            </button>
          </div>
          <ul className="list">
            {waiting.map((item) => (
              <li key={item.key} className="row static">
                <span className="tx">
                  <span className="t1">{item.title}</span>
                  <span className="t2">
                    Saved {formatDateTime(new Date(item.createdAt).toISOString())}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {refused.length > 0 && (
        <section className="section">
          <div className="section-head">
            <h2 className="sec">Refused by the portal ({refused.length})</h2>
          </div>
          <p className="note">
            These changes stay on this device. Try again after the cause is fixed, or ask an
            administrator.
          </p>
          <ul className="list" style={{ marginTop: 10 }}>
            {refused.map((item) => (
              <li key={item.key} className="row static bad">
                <span className="tx">
                  <span className="t1">{item.title}</span>
                  <span className="t2">{rejectionMessage(item.error)}</span>
                </span>
                <button type="button" className="btn sm" onClick={() => void sendNow(item.key)}>
                  Try again
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
